import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { apiHost } from './helpers';
import { getAuthStorage, isDesktopNativeAuth, storeDesktopAccessToken } from './authStorage';
import { requestDesktopAccess } from '../../features/auth/desktopNativeAuth';

// API 클라이언트 인스턴스 생성
const apiClient = axios.create({
  baseURL: `${apiHost}/api`,
  timeout: 30000, // 기본 타임아웃 30초
});

let refreshPromise: Promise<string> | null = null;
let refreshingToken: string | null = null;
let refreshingStorage: Storage | null = null;

export const isInvalidRefreshError = (error: any): boolean => {
  return error?.response?.status === 401;
};

export const clearAuthAndNotify = () => {
  const storage = getAuthStorage();
  const hadTokens = Boolean(
    storage.getItem('accessToken') || storage.getItem('refreshToken')
  );

  storage.removeItem('accessToken');
  storage.removeItem('refreshToken');

  if (hadTokens) {
    window.dispatchEvent(new CustomEvent('auth-logout'));
  }
};

/** 현재 로그인 시트를 Django에서 해제하고 서버 확인 성공 여부를 반환한다. */
export const revokeCurrentSession = async (): Promise<boolean> => {
  const access = getAuthStorage().getItem('accessToken');
  if (!access) {
    return true;
  }

  try {
    await axios.post(`${apiHost}/api/account/session/logout/`, null, {
      headers: { Authorization: `Bearer ${access}` },
      timeout: 5000,
    });
    return true;
  } catch {
    return false;
  }
};

export const refreshAccessToken = (): Promise<string> => {
  if (isDesktopNativeAuth()) {
    return requestDesktopAccess().then((access) => {
      if (!access) throw new Error('desktop session missing');
      if (isDesktopNativeAuth()) storeDesktopAccessToken(access);
      return access;
    });
  }
  const storage = getAuthStorage();
  const refresh = storage.getItem('refreshToken');
  if (!refresh) {
    return Promise.reject(new Error('refresh token missing'));
  }
  if (refreshPromise && refreshingStorage === storage && refreshingToken === refresh) {
    return refreshPromise;
  }

  const pending = axios
    .post(`${apiHost}/api/token/refresh/`, { refresh })
    .then((response) => {
      if (getAuthStorage() !== storage || storage.getItem('refreshToken') !== refresh) {
        throw new Error('Authentication changed during refresh');
      }
      const access = response.data.access;
      const renewedRefresh = response.data.refresh;
      if (renewedRefresh) {
        storage.setItem('refreshToken', renewedRefresh);
      }
      storage.setItem('accessToken', access);
      return access;
    })
    .finally(() => {
      if (refreshPromise === pending) {
        refreshPromise = null;
        refreshingToken = null;
        refreshingStorage = null;
      }
    });

  refreshPromise = pending;
  refreshingToken = refresh;
  refreshingStorage = storage;
  return pending;
};

// 요청 인터셉터: 모든 요청에 Authorization 헤더 추가
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthStorage().getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터: 401 에러 시 토큰 갱신 시도
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      originalRequest.headers?.Authorization
    ) {
      originalRequest._retry = true;
      const currentAccess = getAuthStorage().getItem('accessToken');
      if (originalRequest.headers.Authorization !== `Bearer ${currentAccess}`) {
        return Promise.reject(error);
      }
      if (!getAuthStorage().getItem('refreshToken') && !isDesktopNativeAuth()) {
        clearAuthAndNotify();
        return Promise.reject(error);
      }

      try {
        const access = await refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        if ((isInvalidRefreshError(refreshError) || (isDesktopNativeAuth() && String(refreshError).includes('desktop session missing')))
          && originalRequest.headers.Authorization === `Bearer ${getAuthStorage().getItem('accessToken')}`) {
          clearAuthAndNotify();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
